// src/components/resources/calculators/FullFunnelCalculator.tsx

'use client'

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '~/lib/utils'
import RiMoneyDollarCircleLine from '~/icons/remix/generated/RiMoneyDollarCircleLine'
import RiPercentLine from '~/icons/remix/generated/RiPercentLine'
import RiUserAddLine from '~/icons/remix/generated/RiUserAddLine'
import RiLineChartLine from '~/icons/remix/generated/RiLineChartLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiAlertLine from '~/icons/remix/generated/RiAlertLine'
import { FAQ } from '~/components/shared/FAQ'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import { NumberInput } from '~/components/shared/NumberInput'
import { formatInputValue } from '~/lib/utils/format/input'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import {
  calculateFunnel,
  validateFunnelInputs,
  type FunnelInputs,
  type FunnelResults,
} from './FullFunnelCalculator.logic'

interface EmailTemplateConfig {
  templateAlias: string
  to: string
  from: string
  templateModel: {
    base: {
      name: string
      logo_url: string
      product_name: string
      product_url: string
      support_email: string
      sender_name: string
      company_name: string
      company_address: string
      email: string
    }
    custom: {
      calculator_type: string
      calculator_results: {
        metrics: FunnelResults
        inputs: FunnelInputs
      } | null
    }
  }
}

interface TooltipInfo {
  title: string
  description: string
  tips: string[]
}

const TOOLTIPS: Record<string, TooltipInfo> = {
  impressions: {
    title: 'Impressions',
    description: 'Total number of times your ads were served across the campaign.',
    tips: ['Use the delivered impressions from your ad platform, not reach', 'Pair with CPM to derive total media spend'],
  },
  cpm: {
    title: 'CPM (Cost Per Mille)',
    description: 'What you pay per 1,000 impressions. Combined with impressions this sets total ad spend.',
    tips: ['Ad spend = CPM × impressions ÷ 1,000', 'Prospecting CPMs run higher than retargeting'],
  },
  ctr: {
    title: 'Click-Through Rate (CTR)',
    description: 'Share of impressions that become clicks. Drives how many people enter the funnel.',
    tips: ['Clicks = impressions × CTR ÷ 100', 'A stronger hook/creative is the fastest CTR lever'],
  },
  cvr: {
    title: 'Conversion Rate (CVR)',
    description: 'Share of clicks that convert (purchase/sign-up). Turns traffic into customers.',
    tips: ['Conversions = clicks × CVR ÷ 100', 'Landing-page relevance and offer clarity move CVR most'],
  },
  aov: {
    title: 'Average Order Value (AOV)',
    description: 'Average revenue per conversion. Multiplies conversions into revenue.',
    tips: ['Revenue = conversions × AOV', 'Bundles and order-minimum thresholds lift AOV'],
  },
  newCustomerRate: {
    title: 'New-Customer Share',
    description: 'The percentage of conversions that are new customers — used to separate CAC from CPA.',
    tips: ['New customers = conversions × new-customer share ÷ 100', 'Set to 100% if every conversion is a new customer'],
  },
  ordersPerCustomer: {
    title: 'Orders per Customer',
    description: 'Average number of lifetime orders a customer places — the repeat-purchase driver of LTV.',
    tips: ['LTV = AOV × orders per customer × gross margin ÷ 100', 'Set to 1 for a single-purchase model'],
  },
  grossMargin: {
    title: 'Gross Margin',
    description: 'Contribution margin on revenue. LTV is computed on a margin (profit) basis, not gross revenue.',
    tips: ['Set to 100% to model revenue-based LTV instead of contribution', 'Excludes COGS, shipping, and payment fees'],
  },
  roas: {
    title: 'Return on Ad Spend (ROAS)',
    description: 'Revenue generated per dollar of media spend. The blended efficiency of the whole funnel.',
    tips: ['ROAS = revenue ÷ ad spend', 'Break-even ROAS depends on your margin, not a fixed benchmark'],
  },
  cpa: {
    title: 'Cost per Acquisition (CPA)',
    description: 'Media cost per conversion, across all conversions (new and returning).',
    tips: ['CPA = ad spend ÷ conversions', 'Compare CPA against first-order contribution (AOV × gross margin ÷ 100) to gauge first-order profitability'],
  },
  cac: {
    title: 'Customer Acquisition Cost (CAC)',
    description: 'Media cost per new customer acquired — always ≥ CPA when some conversions are repeat buyers.',
    tips: ['CAC = ad spend ÷ new customers', 'Judge CAC against LTV, not against a flat target'],
  },
  ltv: {
    title: 'Customer Lifetime Value (LTV)',
    description: 'Contribution profit a customer generates across their lifetime of orders.',
    tips: ['LTV = AOV × orders per customer × gross margin ÷ 100', 'A 3:1 LTV:CAC ratio is the common health target'],
  },
}

const FAQ_ITEMS = [
  {
    question: 'What is a full-funnel marketing calculator?',
    answer:
      'A full-funnel calculator models the entire paid-media path in one place — from impressions and CPM through CTR, CVR, and AOV — and derives the four metrics that matter downstream: ROAS, CPA, CAC, and LTV. Instead of computing each metric in isolation, you can change any upstream knob (say, lift CTR from 1% to 1.5%) and instantly see how ROAS and CAC respond, because every metric shares the same funnel math.',
  },
  {
    question: 'How are ROAS, CPA, CAC, and LTV calculated?',
    answer:
      'Ad spend = CPM × impressions ÷ 1,000. Clicks = impressions × CTR ÷ 100. Conversions = clicks × CVR ÷ 100. Revenue = conversions × AOV. From there: ROAS = revenue ÷ ad spend; CPA = ad spend ÷ conversions; CAC = ad spend ÷ new customers (conversions × new-customer share ÷ 100); and LTV = AOV × orders per customer × gross margin ÷ 100. LTV is computed on a contribution-margin basis, so set gross margin to 100% if you want revenue-based LTV instead.',
  },
  {
    question: "What's the difference between CPA and CAC?",
    answer:
      'CPA (cost per acquisition) is your media cost divided by all conversions. CAC (customer acquisition cost) divides the same spend by new customers only. When some conversions come from returning buyers, fewer of them are new, so CAC is higher than CPA. This calculator exposes a new-customer share so you can separate the two — set it to 100% and CAC equals CPA.',
  },
  {
    question: 'What is a healthy LTV:CAC ratio?',
    answer:
      'The widely used rule of thumb is 3:1 — a customer should be worth about three times what it costs to acquire them, leaving room for overhead and profit after payback. Below roughly 1:1 you lose money on each customer; between 1:1 and 3:1 you are profitable but should improve retention, margin, or conversion before scaling spend. Much above 3:1 can signal you are under-investing in growth.',
  },
  {
    question: 'Why does the first purchase sometimes lose money?',
    answer:
      "It's common — and often fine — for CPA to exceed the first order's contribution profit (AOV × gross margin), meaning the first order does not cover the cost to acquire it. Profitability then depends on repeat purchases (LTV). That's why the calculator surfaces LTV and the LTV:CAC ratio: a first-order loss is sustainable when lifetime value comfortably exceeds acquisition cost, and dangerous when it doesn't.",
  },
]

/**
 * Formatter for funnel volumes (impressions, clicks, conversions, new customers).
 * Rounds to whole numbers for readable magnitudes, but preserves up to two
 * fractional digits for small, non-integer expected counts (e.g. a low-volume
 * funnel that yields 0.36 conversions) so the displayed funnel stays consistent
 * with the finite CPA/CAC computed from those fractional values — a rounded `0`
 * next to a finite "spend ÷ 0 conv." reads as broken.
 */
function formatCount(value: number): string {
  if (isNaN(value)) return 'N/A'
  if (!isFinite(value)) return '∞'
  const showFraction = value > 0 && value < 100 && !Number.isInteger(value)
  if (showFraction) {
    // A tiny-but-positive count (e.g. 0.0036 conversions from a 10-impression
    // funnel) would round to "0" at two digits, reviving the "÷ 0 conv."
    // mismatch this formatter exists to avoid. Show it's nonzero instead.
    if (Math.round(value * 100) / 100 === 0) return '<0.01'
    return formatInputValue(value, { maximumFractionDigits: 2 })
  }
  return formatInputValue(value, { maximumFractionDigits: 0 })
}

export function FullFunnelCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<FunnelInputs>({
    impressions: 500000,
    cpm: 12,
    ctr: 1.2,
    cvr: 3,
    aov: 90,
    newCustomerRate: 70,
    ordersPerCustomer: 3,
    grossMargin: 60,
  })

  const [results, setResults] = useState<FunnelResults | null>(null)
  // Snapshot of the inputs the current `results` were computed from, so the
  // results panel and the emailed analysis never pair live inputs with stale
  // metrics when a visitor edits a field without re-clicking Calculate.
  const [resultInputs, setResultInputs] = useState<FunnelInputs | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [email, setEmail] = useState('')
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null)
  const [validationMessages, setValidationMessages] = useState<Array<{ type: 'warning'; message: string }>>([])
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const _debouncedInteract = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const interactionType = (args[0] as string) ?? 'input_change'
        sendResourceEvent('interact', {
          resourceType: 'calculator',
          resourceSlug: 'full-funnel-calculator',
          engagementMs: getDeltaMs(),
          params: { interaction_type: interactionType },
        })
      }, 400),
    [getDeltaMs],
  )

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const setField = (field: keyof FunnelInputs, value: number): void => {
    setInputs((prev) => ({ ...prev, [field]: value }))
    trackInteract('input_change')
  }

  const handleCalculate = (): void => {
    const validationWarnings = validateFunnelInputs(inputs)
    setValidationMessages(validationWarnings)

    const newResults = calculateFunnel(inputs)
    setResults(newResults)
    setResultInputs(inputs)
    setShowEmailCapture(true)

    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'full-funnel-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' },
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'full-funnel-calculator',
      engagementMs: getTotalMs(),
      params: { roas: newResults.roas, cpa: newResults.cpa, cac: newResults.cac, ltv: newResults.ltv },
    })
  }

  const emailTemplate = useMemo<EmailTemplateConfig>(
    () => ({
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: email ? (email.split('@')[0]?.trim() ?? '') : '',
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
          email: email ?? '',
        },
        custom: {
          calculator_type: 'Full-Funnel Calculator',
          calculator_results: results && resultInputs ? { metrics: results, inputs: resultInputs } : null,
        },
      },
    }),
    [email, results, resultInputs],
  )

  const emailCaptureConfig: EmbeddedEmailCaptureProps = {
    preset: 'indigo',
    content: {
      title: { text: 'Get Your Full-Funnel Breakdown' },
      description: { text: "We'll send your ROAS, CPA, CAC, and LTV analysis with tips to improve each stage." },
      button: {
        text: 'Get Analysis',
        loadingText: 'Preparing Analysis...',
        icon: null,
        className:
          'flex w-auto items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
      },
      input: { label: 'Work Email', placeholder: 'Enter your work email' },
      messages: { success: 'Analysis sent!', error: 'Please enter a valid email address.' },
    },
    template: emailTemplate,
    analytics: {
      enabled: true,
      category: 'calculators',
      action: 'email_capture',
      label: 'full_funnel_calculator_results',
      elementId: 'full-funnel-calculator-email-form',
      pageLocation: 'Full-Funnel Calculator',
    },
    crm: {
      tags: ['calculator_results', 'full_funnel'],
      source: 'full_funnel_calculator',
      // Keys must match the HubSpot contact properties provisioned by
      // `pnpm hubspot:setup-calculators` (scripts/hubspot-setup-calculators.ts).
      // Unprovisioned keys degrade gracefully (addToCRM keeps signup_source),
      // but only provisioned ones actually land — so use the namespaced names.
      customFields: {
        calculator_type: 'Full-Funnel',
        calculator_cpm: resultInputs?.cpm,
        calculator_aov: resultInputs?.aov,
        calculator_roas: results?.roas,
      },
    },
    onSubmit: (submittedEmail) => setEmail(submittedEmail),
  }

  const renderTooltip = (key: string): JSX.Element | null => {
    const info = TOOLTIPS[key]
    if (!info) return null

    const panelId = `tooltip-${key}`
    return (
      <div className="relative ml-2 inline-block">
        <button
          type="button"
          className="rounded text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          aria-label={`Learn more about ${info.title}`}
          aria-expanded={activeTooltip === key}
          aria-controls={panelId}
          // Associate the open panel as the button's description so a screen
          // reader announces the tips on focus. Only when open — the panel is
          // conditionally rendered, and a dangling aria-describedby id is invalid.
          aria-describedby={activeTooltip === key ? panelId : undefined}
          // Reveal on hover AND keyboard/touch focus so the help is reachable
          // for mouse, keyboard, and touch users (not hover-only). No onClick
          // toggle: a tap focuses the button first (firing onFocus, which opens
          // the tooltip), so a click handler would immediately toggle it back
          // shut — leaving touch users unable to open it. Focus + blur covers tap.
          onMouseEnter={() => setActiveTooltip(key)}
          onMouseLeave={() => setActiveTooltip(null)}
          onFocus={() => setActiveTooltip(key)}
          onBlur={() => setActiveTooltip(null)}
        >
          <RiInformationLine className="h-5 w-5" />
        </button>
        {activeTooltip === key && (
          <div id={panelId} role="tooltip" className="absolute -right-4 top-8 z-10 w-72 rounded-lg bg-white p-4 text-sm shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800">
            <p className="font-medium text-gray-900 dark:text-white">{info.title}</p>
            <p className="mt-2 text-gray-600 dark:text-gray-400">{info.description}</p>
            <ul className="mt-3 space-y-2">
              {info.tips.map((tip, i) => (
                <li key={i} className="flex items-start text-xs text-gray-500 dark:text-gray-400">
                  <RiLightbulbLine className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-400" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  /** Formats a metric value, handling NaN/Infinity and capping absurd values. */
  const formatMetric = (value: number, isCurrency = false, maxValue = 1000000000): string => {
    if (isNaN(value)) return 'N/A'
    if (!isFinite(value)) return value > 0 ? '∞' : '-∞'
    if (Math.abs(value) > maxValue) value = value > 0 ? maxValue : -maxValue
    return formatInputValue(value, {
      isCurrency,
      minimumFractionDigits: isCurrency ? 2 : 1,
      maximumFractionDigits: 2,
    })
  }

  const ratioLabel = (value: number): string => {
    if (isNaN(value)) return 'N/A'
    if (!isFinite(value)) return '∞'
    return `${value.toFixed(2)}x`
  }

  const inputField = (
    field: keyof FunnelInputs,
    opts: {
      label: string
      isCurrency?: boolean
      min?: number
      description: string
      placeholder: string
      showCents?: boolean
      inputMode?: 'decimal' | 'numeric'
    },
  ): JSX.Element => (
    <div>
      <div className="flex items-center">
        <label htmlFor={field} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {opts.label}
        </label>
        {renderTooltip(field)}
      </div>
      <NumberInput
        id={field}
        value={inputs[field]}
        onChange={(value) => setField(field, value)}
        // The visible field label lives outside NumberInput (paired with the
        // tooltip trigger above), so pass an empty label to suppress the
        // component's own <label> and avoid a duplicate for the same input.
        label=""
        isCurrency={opts.isCurrency ?? false}
        // Preserve decimals in the visible control (CTR 1.2, CPM 12.50, 2.3
        // orders). `showCents={false}` would format the committed value with
        // maximumFractionDigits: 0, rounding the display away from the value
        // the calculator actually uses. Only whole-count fields opt out.
        showCents={opts.showCents ?? true}
        // Non-currency rate fields (CTR/CVR/share/orders/margin) are still
        // decimal-capable, so surface a decimal keypad on mobile rather than the
        // integer-only numeric one. Whole-count fields pass 'numeric'.
        inputMode={opts.inputMode ?? 'decimal'}
        min={opts.min ?? 0}
        placeholder={opts.placeholder}
        description={opts.description}
      />
    </div>
  )

  const metricCard = (
    label: string,
    tooltipKey: string,
    valueText: string,
    subText: string,
    emphasize = false,
  ): JSX.Element => (
    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
      <dt className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
        <span>{label}</span>
        {renderTooltip(tooltipKey)}
      </dt>
      <dd
        className={cn(
          'mt-2 text-2xl font-semibold truncate',
          emphasize ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white',
        )}
      >
        {valueText}
      </dd>
      <p className="mt-1 text-xs text-gray-500">{subText}</p>
    </div>
  )

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Full-Funnel Marketing Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Model impressions → clicks → conversions → revenue in one place, and see ROAS, CPA, CAC, and LTV update as you
            change any input.
          </p>
        </div>

        <div className="mt-8 space-y-8 px-8 pb-8">
          {/* Media */}
          <fieldset className="space-y-6">
            <legend className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Media</legend>
            <div className="grid gap-6 sm:grid-cols-2">
              {inputField('impressions', {
                label: 'Impressions',
                description: 'Total impressions delivered',
                placeholder: 'e.g. 500,000',
                showCents: false, // whole-count field
                inputMode: 'numeric', // integer keypad — impressions are whole
              })}
              {inputField('cpm', {
                label: 'CPM ($)',
                isCurrency: true,
                description: 'Cost per 1,000 impressions',
                placeholder: 'e.g. 12',
              })}
            </div>
          </fieldset>

          {/* Funnel */}
          <fieldset className="space-y-6">
            <legend className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Funnel</legend>
            <div className="grid gap-6 sm:grid-cols-2">
              {inputField('ctr', {
                label: 'Click-Through Rate (%)',
                description: 'Share of impressions that click',
                placeholder: 'e.g. 1.2',
              })}
              {inputField('cvr', {
                label: 'Conversion Rate (%)',
                description: 'Share of clicks that convert',
                placeholder: 'e.g. 3',
              })}
            </div>
          </fieldset>

          {/* Economics */}
          <fieldset className="space-y-6">
            <legend className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Economics</legend>
            <div className="grid gap-6 sm:grid-cols-2">
              {inputField('aov', {
                label: 'Average Order Value ($)',
                isCurrency: true,
                description: 'Average revenue per conversion',
                placeholder: 'e.g. 90',
              })}
              {inputField('newCustomerRate', {
                label: 'New-Customer Share (%)',
                description: 'Conversions that are new customers',
                placeholder: 'e.g. 70',
              })}
              {inputField('ordersPerCustomer', {
                label: 'Orders per Customer',
                description: 'Lifetime orders per customer',
                placeholder: 'e.g. 3',
              })}
              {inputField('grossMargin', {
                label: 'Gross Margin (%)',
                description: 'Contribution margin for LTV',
                placeholder: 'e.g. 60',
              })}
            </div>
          </fieldset>
        </div>

        {validationMessages.length > 0 && (
          <div className="px-8 py-2 pb-6">
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <div className="flex">
                <div className="flex-shrink-0">
                  <RiAlertLine className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Input Validation Warnings</h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                    <ul className="list-disc space-y-1 pl-5">
                      {validationMessages.map((warning, index) => (
                        <li key={index}>{warning.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-b-2xl border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate full-funnel metrics"
          >
            Calculate Full-Funnel Metrics
          </button>
        </div>
      </div>

      <AnimatePresence>
        {results && resultInputs && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
            role="region"
            aria-label="Calculation Results"
          >
            {/* Funnel at a glance */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Your Funnel at a Glance</h3>
              <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Impressions</dt>
                  <dd className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{formatCount(resultInputs.impressions)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Clicks</dt>
                  <dd className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{formatCount(results.clicks)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Conversions</dt>
                  <dd className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{formatCount(results.conversions)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Ad Spend</dt>
                  <dd className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{formatMetric(results.adSpend, true)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Revenue</dt>
                  <dd className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{formatMetric(results.revenue, true)}</dd>
                </div>
              </dl>
            </div>

            {/* Headline metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Headline Metrics</h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {metricCard('ROAS', 'roas', ratioLabel(results.roas), `${formatMetric(results.revenue, true)} ÷ ${formatMetric(results.adSpend, true)}`, true)}
                {metricCard('CPA', 'cpa', formatMetric(results.cpa, true), `${formatMetric(results.adSpend, true)} ÷ ${formatCount(results.conversions)} conv.`)}
                {metricCard('CAC', 'cac', formatMetric(results.cac, true), `${formatMetric(results.adSpend, true)} ÷ ${formatCount(results.newCustomers)} new cust.`)}
                {metricCard('LTV', 'ltv', formatMetric(results.ltv, true), `${formatMetric(resultInputs.aov, true)} × ${resultInputs.ordersPerCustomer} × ${resultInputs.grossMargin}%`)}
              </dl>

              {/* LTV:CAC health ratio */}
              <div className="mt-4 rounded-lg bg-indigo-50 p-5 dark:bg-indigo-900/20">
                <div className="flex items-center">
                  <RiMoneyDollarCircleLine className="mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                  <h4 className="font-medium text-indigo-800 dark:text-indigo-300">
                    LTV:CAC —{' '}
                    <span className="font-semibold">
                      {isNaN(results.ltvToCac) ? 'N/A' : !isFinite(results.ltvToCac) ? '∞' : `${results.ltvToCac.toFixed(1)}:1`}
                    </span>
                  </h4>
                </div>
                <p className="mt-2 text-sm text-indigo-700 dark:text-indigo-300">
                  {!isFinite(results.ltvToCac) || isNaN(results.ltvToCac)
                    ? 'Enter a non-zero new-customer volume and margin to evaluate unit economics.'
                    : results.ltvToCac >= 3
                      ? 'At or above the 3:1 rule of thumb — healthy unit economics with room to scale acquisition.'
                      : results.ltvToCac >= 1
                        ? 'Profitable but below the 3:1 target — improve retention, margin, or conversion before scaling spend.'
                        : "Below 1:1 — you're paying more to acquire a customer than they're worth. Fix conversion, AOV, or retention first."}
                </p>
              </div>
            </div>

            {/* Recommendations */}
            {results.recommendations.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Insights &amp; Recommendations</h3>
                <div className="mt-4 space-y-3">
                  {results.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className={cn(
                        'rounded-lg p-4',
                        rec.type === 'primary'
                          ? 'bg-indigo-50 dark:bg-indigo-900/20'
                          : rec.type === 'optimization'
                            ? 'bg-amber-50 dark:bg-amber-900/20'
                            : 'bg-gray-50 dark:bg-gray-800',
                      )}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <span
                            className={cn(
                              'flex h-8 w-8 items-center justify-center rounded-full',
                              rec.type === 'primary'
                                ? 'bg-indigo-100 dark:bg-indigo-800'
                                : rec.type === 'optimization'
                                  ? 'bg-amber-100 dark:bg-amber-800'
                                  : 'bg-gray-100 dark:bg-gray-700',
                            )}
                          >
                            {rec.icon === 'chart' && <RiLineChartLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                            {rec.icon === 'acquisition' && <RiUserAddLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {rec.icon === 'money' && <RiMoneyDollarCircleLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                            {rec.icon === 'strategy' && <RiPercentLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {rec.icon === 'warning' && <RiAlertLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {!rec.icon && <RiLightbulbLine className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                          </span>
                        </div>
                        <p
                          className={cn(
                            'ml-4 text-sm',
                            rec.type === 'primary'
                              ? 'text-indigo-700 dark:text-indigo-300'
                              : rec.type === 'optimization'
                                ? 'text-amber-700 dark:text-amber-300'
                                : 'text-gray-700 dark:text-gray-300',
                          )}
                        >
                          {rec.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showEmailCapture && results && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                <EmbeddedEmailCapture {...emailCaptureConfig} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-white">Frequently Asked Questions</h3>
        <FAQ items={FAQ_ITEMS} type="single" disableSchema={true} features={{ showTitle: false }} theme={{ size: 'lg' }} />
      </div>
    </div>
  )
}

export default FullFunnelCalculator
